// Every dashboard websocket handler drops a message whose `sessionKey` is not the hub's current one — that filter is
// what keeps a user's hub state on the session they are actually looking at. A dozen handlers share the rule, so ONE
// foreign message costs a dozen identical "Skip handle WS message" lines, and a foreign SESSION costs that per
// message for as long as it lives. Two of those are entirely legitimate and routine:
//
//   * a packing move (MytaStreams/seatMove.ts) INITs a second UE under its own key and keeps it for a minute;
//   * right after the switch, the seat that was left behind keeps talking until it is torn down.
//
// Measured 2026-09-17: a two-minute visit produced several hundred of these lines and buried everything else in the
// console, which is what the operator reported as log spam. The filtering itself is right — only the narration is
// wrong, so this collapses a run of skips from the same session into one line plus a periodic summary.
//
// Pure, so the rule is testable; the module-level instance below is what the handlers call.

export const SKIP_LOG_SUMMARY_MS = 10_000;

export type SkipLogDecision =
  | { say: 'nothing' }
  | { say: 'first'; sessionKey: string; expected: string }
  | {
      say: 'summary';
      sessionKey: string;
      expected: string;
      suppressed: number;
      windowMs: number;
    };

export interface SessionSkipLog {
  note: (sessionKey: string, expected: string, now: number) => SkipLogDecision;
}

/**
 * One run of skipped messages = one session key being dropped against one expected key. The first skip is always
 * said; the rest are counted and summarised no more often than `summaryMs`. A different session (or a new expected
 * key — the hub adopted one at a switch) starts a new run, and the old run's leftovers are summarised then.
 */
export const createSessionSkipLog = (
  summaryMs: number = SKIP_LOG_SUMMARY_MS,
): SessionSkipLog => {
  let run: {
    sessionKey: string;
    expected: string;
    suppressed: number;
    windowStartedAt: number;
  } | null = null;

  return {
    note: (sessionKey, expected, now) => {
      if (!run || run.sessionKey !== sessionKey || run.expected !== expected) {
        const previous =
          run && run.suppressed > 0
            ? ({
                say: 'summary',
                sessionKey: run.sessionKey,
                expected: run.expected,
                suppressed: run.suppressed,
                windowMs: now - run.windowStartedAt,
              } as const)
            : null;
        run = { sessionKey, expected, suppressed: 0, windowStartedAt: now };

        // A new session's first skip is worth a line of its own; when it displaces a run that still had
        // uncounted messages, the summary of the old one is the more informative of the two.
        return previous ?? { say: 'first', sessionKey, expected };
      }

      run.suppressed += 1;

      if (now - run.windowStartedAt < summaryMs) {
        return { say: 'nothing' };
      }

      const decision = {
        say: 'summary',
        sessionKey: run.sessionKey,
        expected: run.expected,
        suppressed: run.suppressed,
        windowMs: now - run.windowStartedAt,
      } as const;
      run = { sessionKey, expected, suppressed: 0, windowStartedAt: now };

      return decision;
    },
  };
};

export const describeSkipDecision = (
  decision: SkipLogDecision,
): string | null => {
  if (decision.say === 'nothing') {
    return null;
  }

  if (decision.say === 'first') {
    return `Skip handle WS message for sessionKey: ${decision.sessionKey}, expected sessionKey: ${decision.expected} — further ones from that session are summarised`;
  }

  return `Skipped ${decision.suppressed} more WS message${
    decision.suppressed === 1 ? '' : 's'
  } for sessionKey: ${decision.sessionKey}, expected sessionKey: ${
    decision.expected
  } (last ${Math.round(decision.windowMs / 1000)}s)`;
};
